// src/components/Onboarding/ProgressBar.js

import React from 'react';

/**
 * PROGRESS BAR COMPONENT
 * 
 * Shows current progress through the onboarding flow.
 * Displays as a progress bar that fills as user progresses with smooth animations.
 * Theme-aware colors for light and dark backgrounds.
 * 
 * HOW TO USE:
 * <ProgressBar current={2} total={10} theme="dark" />
 * 
 * PROPS:
 * - current: Current step (0-based)
 * - total: Total number of steps
 * - theme: 'light' or 'dark' for theme-aware styling (default: 'light')
 * 
 * VISUAL:
 * - Progress bar that fills from left to right showing completion percentage
 * - Smooth animations when progress changes between ALL slides
 * - Theme-aware colors: light theme uses dark fill, dark theme uses white fill
 * - Consistent animation timing and easing
 */

export default function ProgressBar({ current, total, theme = 'light' }) {
  // Calculate progress percentage (add 1 to current for 1-based display)
  const progressPercent = ((current + 1) / total) * 100;
  
  // Theme-aware styling
  const progressStyles = {
    light: {
      background: 'bg-gray-200',
      fill: 'bg-mm_black'
    },
    dark: {
      background: 'bg-gray-600', 
      fill: 'bg-white'
    }
  };
  
  const currentStyle = progressStyles[theme];
  
  return (
    <div className="flex items-center w-full">
      {/* Progress Bar Container */}
      <div className={`w-full h-1 ${currentStyle.background} rounded-full overflow-hidden`}>
        {/* Progress Fill - Key: Force re-render with key prop to ensure animation works */}
        <div 
          key={`progress-${current}-${total}`}
          className={`h-full ${currentStyle.fill} rounded-full transition-all duration-500 ease-out`}
          style={{ 
            width: `${progressPercent}%`,
            // Force hardware acceleration for smoother animation
            transform: 'translateZ(0)',
            willChange: 'width'
          }}
        />
      </div>
    </div>
  );
}